"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parse,
  setMonth,
  setYear,
  startOfDay,
  startOfMonth,
} from "date-fns"
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"

import { cn } from "@/lib/utils"

import { Select } from "./Select"

interface DatePickerProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  className?: string
  name?: string
  autocomplete?: string
  /** "sm" matches 44px buttons, like Select's small size. */
  size?: "sm" | "md"
}

/** Approximate height of the open calendar, to decide which way it opens. */
const CALENDAR_HEIGHT = 400

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]

const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
]

function generateYearRange(minYear: number, maxYear: number): number[] {
  const years: number[] = []
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year)
  }
  return years
}

function displayFromIsoDate(value: string): {
  input: string
  month: Date | null
} {
  if (!value) return { input: "", month: null }
  const date = parse(value, "yyyy-MM-dd", new Date())
  if (!isValid(date)) return { input: "", month: null }
  return { input: format(date, "dd/MM/yyyy"), month: date }
}

export function DatePicker({
  value,
  onChange,
  placeholder = "DD/MM/AAAA",
  disabled = false,
  minDate,
  maxDate = new Date(),
  className,
  name,
  autocomplete,
  size = "md",
}: DatePickerProps) {
  const [open, setOpen] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>(
    () => displayFromIsoDate(value).input
  )
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      return parse(value, "yyyy-MM-dd", new Date())
    }
    // Default to 30 years ago for birth date picker
    const defaultDate = new Date()
    defaultDate.setFullYear(defaultDate.getFullYear() - 30)
    return defaultDate
  })
  const [valueSnapshot, setValueSnapshot] = useState(value)
  if (value !== valueSnapshot) {
    setValueSnapshot(value)
    const next = displayFromIsoDate(value)
    setInputValue(next.input)
    if (next.month) setCurrentMonth(next.month)
  }

  const containerRef = useRef<HTMLDivElement>(null)
  // Opens upward when the calendar would run past the bottom of the screen
  // (a field near the end of a dialog), downward otherwise.
  const [openUp, setOpenUp] = useState(false)
  useLayoutEffect(() => {
    if (!open || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom
    setOpenUp(below < CALENDAR_HEIGHT && rect.top > below)
  }, [open])
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedDate = value ? parse(value, "yyyy-MM-dd", new Date()) : null

  // Generate year range (100 years back from maxDate)
  const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear()
  const minYear = minDate ? minDate.getFullYear() : maxYear - 100
  const years = generateYearRange(minYear, maxYear)

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Element
      // The month/year Select renders its list in a portal.
      if (target.closest?.("[data-radix-popper-content-wrapper]")) return
      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open])

  const handleDayClick = (day: Date) => {
    onChange(format(day, "yyyy-MM-dd"))
    setOpen(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value

    // Remove non-digit characters
    const digitsOnly = input.replace(/\D/g, "")

    // Auto-format as DD/MM/YYYY
    let formatted = ""
    if (digitsOnly.length > 0) {
      formatted = digitsOnly.substring(0, 2)
    }
    if (digitsOnly.length > 2) {
      formatted += "/" + digitsOnly.substring(2, 4)
    }
    if (digitsOnly.length > 4) {
      formatted += "/" + digitsOnly.substring(4, 8)
    }

    setInputValue(formatted)

    // Try to parse complete date
    if (digitsOnly.length === 8) {
      const day = digitsOnly.substring(0, 2)
      const month = digitsOnly.substring(2, 4)
      const year = digitsOnly.substring(4, 8)

      const parsedDate = parse(
        `${year}-${month}-${day}`,
        "yyyy-MM-dd",
        new Date()
      )

      if (isValid(parsedDate)) {
        const isBeforeMin = minDate && isBefore(parsedDate, minDate)
        const isAfterMax = maxDate && isAfter(parsedDate, maxDate)

        if (!isBeforeMin && !isAfterMax) {
          onChange(format(parsedDate, "yyyy-MM-dd"))
          setCurrentMonth(parsedDate)
        }
      }
    }
  }

  const handleInputBlur = () => {
    // Reset to current value if input is invalid
    if (value) {
      const date = parse(value, "yyyy-MM-dd", new Date())
      if (isValid(date)) {
        setInputValue(format(date, "dd/MM/yyyy"))
      }
    } else if (inputValue && inputValue.length < 10) {
      setInputValue("")
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex w-full items-center overflow-hidden rounded-xl border border-background-200 bg-white",
          "focus-within:border-primary-600 focus-within:ring-[3px] focus-within:ring-primary-500/50",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={() => !disabled && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          name={name}
          autoComplete={autocomplete}
          className={cn(
            "min-w-0 flex-1 bg-transparent outline-none",
            size === "sm" ? "px-3 py-2 text-sm" : "text-body px-4 py-3",
            "placeholder:text-text-400",
            disabled && "cursor-not-allowed"
          )}
        />
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          className={cn(
            "shrink-0 transition-colors hover:bg-background-100",
            size === "sm" ? "px-2.5 py-2" : "px-3 py-3"
          )}
          tabIndex={-1}
          disabled={disabled}
          aria-label="Abrir calendário"
        >
          <CalendarIcon
            className={cn(
              "text-text-400",
              size === "sm" ? "h-4 w-4" : "h-5 w-5"
            )}
          />
        </button>
      </div>

      {open && (
        <div
          className={cn(
            "absolute left-0 z-50 w-[320px] rounded-xl border border-background-100 bg-white p-4 shadow-lg",
            openUp ? "bottom-full mb-2" : "top-full mt-2"
          )}
        >
          <Calendar
            value={selectedDate}
            onSelect={handleDayClick}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            minDate={minDate}
            maxDate={maxDate}
            years={years}
          />
        </div>
      )}
    </div>
  )
}

function isOutside(day: Date, minDate?: Date, maxDate?: Date): boolean {
  if (minDate && isBefore(day, startOfDay(minDate))) return true
  if (maxDate && isAfter(day, maxDate)) return true
  return false
}

const MONTH_OPTIONS = MONTHS.map((month, index) => ({
  value: String(index),
  label: month,
}))

/**
 * The month grid of the date picker, also usable inline (the schedule
 * dialog). `years` shows month and year Selects for jumping far (birth
 * dates); without it the header is the month name between arrows, which
 * the arrows keep inside min/max.
 */
export function Calendar({
  value,
  onSelect,
  month,
  onMonthChange,
  minDate,
  maxDate,
  years,
}: {
  value: Date | null
  onSelect: (day: Date) => void
  month: Date
  onMonthChange: (month: Date) => void
  minDate?: Date
  maxDate?: Date
  years?: number[]
}) {
  const monthStart = startOfMonth(month)
  const days = eachDayOfInterval({ start: monthStart, end: endOfMonth(month) })
  const paddingDays = Array(getDay(monthStart)).fill(null)
  const shift = (delta: number) => {
    const next = new Date(month)
    next.setDate(1)
    next.setMonth(next.getMonth() + delta)
    onMonthChange(next)
  }
  const canGoBack = !minDate || isAfter(monthStart, minDate)
  const canGoForward = !maxDate || isBefore(endOfMonth(month), maxDate)

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => shift(-1)}
          disabled={!canGoBack}
          className="shrink-0 rounded-lg p-2 transition-colors hover:bg-background-100 disabled:opacity-30"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="h-4 w-4 text-text-900" />
        </button>

        {years ? (
          <>
            <Select
              size="sm"
              value={String(month.getMonth())}
              onChange={(next) => onMonthChange(setMonth(month, Number(next)))}
              options={MONTH_OPTIONS}
              aria-label="Mês"
              className="flex-1"
            />
            <Select
              size="sm"
              value={String(month.getFullYear())}
              onChange={(next) => onMonthChange(setYear(month, Number(next)))}
              options={years.map((year) => ({
                value: String(year),
                label: String(year),
              }))}
              className="w-24"
              aria-label="Ano"
            />
          </>
        ) : (
          <p className="flex-1 text-center text-sm font-semibold text-text-900">
            {MONTHS[month.getMonth()]} de {month.getFullYear()}
          </p>
        )}

        <button
          type="button"
          onClick={() => shift(1)}
          disabled={!canGoForward}
          className="shrink-0 rounded-lg p-2 transition-colors hover:bg-background-100 disabled:opacity-30"
          aria-label="Próximo mês"
        >
          <ChevronRight className="h-4 w-4 text-text-900" />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="text-caption flex h-8 items-center justify-center font-medium text-text-500"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {paddingDays.map((_, index) => (
          <div key={`padding-${index}`} className="h-9" />
        ))}
        {days.map((day) => {
          const isSelected = value !== null && isSameDay(day, value)
          const isDisabled = isOutside(day, minDate, maxDate)
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => !isDisabled && onSelect(day)}
              disabled={isDisabled}
              aria-pressed={isSelected}
              className={cn(
                "text-body-sm h-9 w-full rounded-lg text-text-900 transition-colors",
                isSelected && "bg-primary-600 font-medium text-white",
                !isSelected && !isDisabled && "hover:bg-primary-50",
                isDisabled && "cursor-not-allowed opacity-30"
              )}
            >
              {format(day, "d")}
            </button>
          )
        })}
      </div>
    </div>
  )
}
